import {
  BoxV2 as Box,
  Button,
  Flex,
  Table,
  FilterConfig,
  FilterType,
  TableFilter,
  useFilters,
  useTable,
  RoleGuard,
} from 'portal-commons';
import React, { useEffect, useState } from 'react';
import { faFilter, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RcsCampaignListingRow } from '../components';
import { EmptyFetchedData } from '../constants';
import { RcsCampaignListItem } from '../types';
import { CampaignFilterErrorCode as FilterErrorCode } from '../../index';
import { PageTitle } from '@/components';
import Loader from '@/shared_elements/containers/Loader';
import { fieldValidation } from '@/utils/validator';
import { campaignController, enumController } from '@/apis/core';
import {
  getCnpListOptions,
  getBillingCategoryOptions,
  getBrandNameAutocompleteOptions,
} from '@/apis/services';
import { UsecaseType } from '@/utils/types';
import { useHistory } from 'react-router-dom';

const FilterConfigs: Record<string, FilterConfig> = {
  campaignUid: {
    type: FilterType.Text,
    label: 'Campaign ID',
    placeholder: 'Enter campaign id',
    width: 150,
  },
  // displayName: {
  //   type: FilterType.Text,
  //   label: 'Display Name',
  //   placeholder: 'Enter display name',
  //   suggestions: [],
  //   width: 200,
  // },
  brandUid: {
    type: FilterType.Text,
    label: 'Brand ID',
    placeholder: 'Enter brand id',
    width: 150,
  },
  brandName: {
    type: FilterType.Text,
    label: 'Brand DBA Name',
    placeholder: 'Enter brand DBA name',
    suggestions: [],
    width: 260,
  },
  usecase: {
    type: FilterType.Dropdown,
    label: 'Use Case',
    placeholder: 'Select use case',
    options: [],
    width: 250,
  },
  billingCategory: {
    type: FilterType.Dropdown,
    label: 'Billing Category',
    placeholder: 'Select billing category',
    options: [],
    width: 230,
  },
  upstreamCnpUid: {
    type: FilterType.Dropdown,
    label: 'Upstream CNP',
    placeholder: 'Select upstream CNP',
    options: [],
    width: 220,
  },
  status: {
    type: FilterType.Dropdown,
    label: 'TCR Status',
    placeholder: 'Select TCR status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Deactivated', value: 'EXPIRED' },
    ],
    width: 170,
  },
  // verificationStatus: {
  //   type: FilterType.Dropdown,
  //   label: 'Verification Status',
  //   placeholder: 'Select verification status',
  //   options: [],
  //   width: 190,
  // },
};

const FilterLoaders = {
  suggestion: {
    brandName: {
      minLength: 2,
      load: (value: string) => getBrandNameAutocompleteOptions(value),
    },
  },
};

const headRows = [
  { id: 'uid', label: 'Campaign ID', sortable: true },
  // { id: 'displayName', label: 'Display Name', sortable: true },
  { id: 'brandUid', label: 'Brand ID', sortable: true },
  { id: 'brandName', label: 'Brand DBA Name', sortable: false },
  { id: 'usecase', label: 'Use Case', sortable: true },
  { id: 'billingCategory', label: 'Billing Category', sortable: true },
  { id: 'upstreamCnpName', label: 'Upstream CNP', sortable: false },
  { id: 'createDate', label: 'Registered Date', sortable: true },
  { id: 'status', label: 'TCR Status', sortable: true },
];

export interface ErrorCodeType {
  [name: string]: {
    [key: number | string]: string | boolean | number;
  };
}

const RcsCampaigns: React.FC = () => {
  const history = useHistory();
  const {
    configs: filterConfigs,
    candidateFilters,
    appliedFilters,
    additionalFilters,
    handleEdit: handleFiltersEdit,
    handleApply: handleFiltersApply,
    setOptions: setFilterOptions,
    updateConfigObject: updateFilterConfigs,
  } = useFilters({
    configs: FilterConfigs,
    loaders: FilterLoaders,
  });
  const [filtersShown, setFiltersShown] = useState(false);
  const [rcsUsecaseTypes, setRcsUsecaseTypes] = useState<UsecaseType[]>([]);

  const fetchCampaigns = async () => {
    const queries = getQueries();

    const response = await campaignController.getRcsCampaigns(queries);
    if (response) {
      return {
        data: response.records,
        rowsPerPage: response.recordsPerPage,
        total: response.totalRecords,
        page: response.page,
      };
    }
    return EmptyFetchedData;
  };

  const {
    loading: tableLoading,
    pageLoading,
    total: totalCampaigns,
    handleSorting: handleSortingChange,
    handleChangePage: handlePageChange,
    tableData: campaigns,
    pagination,
    filter: tableFilters,
  } = useTable<RcsCampaignListItem>(fetchCampaigns, {
    deps: [appliedFilters, additionalFilters],
    defaultFilter: {
      sortField: 'createDate',
      ascendingOrder: false,
    },
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getQueries = (): Record<string, any> => {
    return {
      ...tableFilters,
      ...appliedFilters,
      ...additionalFilters,
    };
  };

  const fetchFilterOptions = async () => {
    const cnpOptions = await getCnpListOptions();
    setFilterOptions('upstreamCnpUid', cnpOptions);

    const billingCategoryOptions = await getBillingCategoryOptions();
    setFilterOptions('billingCategory', billingCategoryOptions);

    const useCaseTypes = await enumController.getUseCases({
      channelId: 'RCS',
    });
    setRcsUsecaseTypes(useCaseTypes);
    const rcsUsecaseOptions = useCaseTypes.map((useCase) => ({
      label: useCase.displayName,
      value: useCase.id,
    }));
    setFilterOptions('usecase', rcsUsecaseOptions);

    // const verificationStatusOptions = await getVerificationStatusOptions();
    // setFilterOptions('verificationStatus', verificationStatusOptions);
  };

  const resetFilterConfigsError = (newValues: Record<string, string>) => {
    const tempFilterConfigs = { ...filterConfigs };
    Object.keys(newValues).forEach((key) => {
      if (
        tempFilterConfigs[key]?.error &&
        newValues[key] !== candidateFilters[key]
      ) {
        tempFilterConfigs[key].error = false;
        tempFilterConfigs[key].helperText = '';
      }
    });
    updateFilterConfigs(tempFilterConfigs);
  };

  const validateFilters = (values: Record<string, string>) => {
    const tempFilterConfigs = { ...filterConfigs };
    Object.keys(values).forEach((key) => {
      if (key in FilterErrorCode) {
        const code = fieldValidation({
          ...(FilterErrorCode as ErrorCodeType)[`${key}Obj`],
          fieldval: values[key],
        });
        if (code !== 0) {
          tempFilterConfigs[key].error = true;
          tempFilterConfigs[key].helperText = String(
            (FilterErrorCode as ErrorCodeType)[key][code]
          );
        }
      }
    });
    updateFilterConfigs(tempFilterConfigs);
    return Object.keys(values).every((key) => !tempFilterConfigs[key].error);
  };

  const handleCandidateFiltersChange = (values: Record<string, string>) => {
    resetFilterConfigsError(values);
    handleFiltersEdit(values);
  };

  const handleAppliedFiltersChange = (values: Record<string, string>) => {
    if (!validateFilters(values)) {
      return;
    }

    if (Object.keys(values).length === 0) {
      resetFilterConfigsError(
        filterConfigs as unknown as Record<string, string>
      );
    }
    handleFiltersApply(values);
  };

  return (
    <>
      <Box
        as="section"
        sx={{
          // Fix dropdown width affected by global styling
          '& .MuiAutocomplete-root': {
            width: 'unset',
          },
        }}
        data-testid="rcsCampaignListing"
      >
        {pageLoading ? (
          <Loader />
        ) : (
          <Flex
            sx={{
              flexDirection: 'column',
              alignItems: 'flex-start',
              rowGap: '12px',
            }}
          >
            <PageTitle>
              {totalCampaigns > 0
                ? `${totalCampaigns} RCS Campaigns`
                : 'RCS Campaigns'}
            </PageTitle>
            <Flex
              sx={{
                alignItems: 'center',
                columnGap: '12px',
                width: '100%',
                marginTop: '-8px',
                justifyContent: 'space-between',
              }}
            >
              <Button
                color="secondary"
                onClick={() => setFiltersShown(!filtersShown)}
                data-testid="tableAddFilterButton"
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>{filtersShown ? 'Hide' : 'Show'} Filters</span>
              </Button>
              <RoleGuard feature="rcsCampaignList.addCampaign">
                <Button
                  variant="outline"
                  onClick={() => history.push('/campaign/create')}
                  data-testid="addRcsCampaignButton"
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Add Campaign
                </Button>
              </RoleGuard>
            </Flex>
            {filtersShown && (
              <TableFilter
                configs={filterConfigs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={(values: Record<string, string>) =>
                  handleCandidateFiltersChange(values)
                }
                onAppliedValuesChange={(values: Record<string, string>) =>
                  handleAppliedFiltersChange(values)
                }
              />
            )}
            <Table
              testId="rcsCampaignsListingTable"
              loading={tableLoading}
              emptyState="no RCS Campaigns to view"
              headRows={headRows}
              tableData={campaigns.map((campaign: RcsCampaignListItem) => (
                <RcsCampaignListingRow
                  key={campaign.uid}
                  campaign={campaign}
                  rcsUseCases={rcsUsecaseTypes}
                />
              ))}
              handleChangePage={handlePageChange}
              createSortHandler={handleSortingChange}
              filter={tableFilters}
              pagination={pagination}
              headerTextTransform="none"
            />
          </Flex>
        )}
      </Box>
    </>
  );
};

export default RcsCampaigns;
